import { defineStore } from 'pinia'
import { ref } from 'vue'
import {
  fetchItems,
  createAutomation,
  fetchItem,
  updateAutomation,
  deleteAutomation,
  disableDefaultAutomation,
  fetchEvents,
  fetchEventSettings
} from '@/api/automations'
import {
  IAutomation,
  IAutomationCreatePayload,
  IAutomationUpdatePayload,
  IAutomationEventOption,
  IAutomationEventSettingPayload,
  IAutomationEventSettingsResponse
} from '@/interfaces/automations'

export const useAutomationsStore = defineStore('automations', () => {
  // State
  const items = ref<{ [itemId: string]: IAutomation }>({})
  const total = ref<{ [typeId: string]: number }>({})
  const sum = ref<{ [typeId: string]: number }>({})
  const listLoading = ref(false)
  const events = ref<IAutomationEventOption[]>([])
  const transports = ref<string[]>([])
  const eventSetting = ref<IAutomationEventSettingsResponse['data'] | null>(
    null
  )

  // Actions
  const FetchItems = async () => {
    listLoading.value = true
    try {
      const response = await fetchItems()
      setItems(response.data)
    } finally {
      listLoading.value = false
    }
  }

  const FetchItem = async (id: string) => {
    listLoading.value = true
    try {
      const response = await fetchItem(id)
      return response.data
    } finally {
      listLoading.value = false
    }
  }

  const CreateAutomation = async (automation: IAutomationCreatePayload) => {
    listLoading.value = true
    try {
      await createAutomation(automation)
    } finally {
      listLoading.value = false
    }
  }

  const UpdateAutomation = async (automation: IAutomationUpdatePayload) => {
    listLoading.value = true
    try {
      await updateAutomation(automation)
    } finally {
      listLoading.value = false
    }
  }

  const DeleteAutomation = async (id: string) => {
    listLoading.value = true
    try {
      await deleteAutomation(id)
      removeItem(id)
    } finally {
      listLoading.value = false
    }
  }

  const DisableDefaultAutomation = async (id: string) => {
    listLoading.value = true
    try {
      await disableDefaultAutomation(id)
      removeItem(id)
    } finally {
      listLoading.value = false
    }
  }

  const getAutomationEvents = async () => {
    listLoading.value = true
    try {
      const response = await fetchEvents()

      events.value = response.events
      transports.value = response.transports
    } finally {
      listLoading.value = false
    }
  }

  const getAutomationEventSettings = async (
    payload: IAutomationEventSettingPayload
  ) => {
    listLoading.value = true
    try {
      const response = await fetchEventSettings(payload)
      eventSetting.value = response.data
      return response.data
    } finally {
      listLoading.value = false
    }
  }

  const setItems = (data: IAutomation[]) => {
    items.value = Object.fromEntries(data.map((item) => [item.id, item]))
  }

  const removeItem = (id: string) => {
    delete items.value[id]
  }

  // Getters
  const getItemsByType = (eventType: string) => {
    return Object.values(items.value).filter(
      (item) => item.attributes.event_type === eventType
    )
  }

  return {
    // State
    items,
    total,
    sum,
    listLoading,
    events,
    eventSetting,
    transports,

    // Actions
    FetchItems,
    CreateAutomation,
    FetchItem,
    UpdateAutomation,
    DeleteAutomation,
    DisableDefaultAutomation,
    getAutomationEvents,
    getAutomationEventSettings,

    // Getters
    getItemsByType
  }
})
